export interface BoardCardReturnState {
  readonly cardId: string;
  readonly stripScrollLeft: number;
  readonly listScrollTop: number;
  readonly returnBoardPath?: string | undefined;
}

export interface BoardCardOpenIntent extends BoardCardReturnState {
  readonly path: string;
  readonly openPanel?: 'dates' | undefined;
}

const CARD_RETURN_PREFIX = 'molt:card-return:';
const BOARD_RETURN_PREFIX = 'molt:board-return:';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parseBoardCardReturn(value: unknown): BoardCardReturnState | undefined {
  if (!isRecord(value)) return undefined;

  const cardId = value.cardId;
  const stripScrollLeft = finiteNumber(value.stripScrollLeft);
  const listScrollTop = finiteNumber(value.listScrollTop);
  const returnBoardPath = value.returnBoardPath;

  if (typeof cardId !== 'string' || stripScrollLeft === null || listScrollTop === null) {
    return undefined;
  }

  return {
    cardId,
    stripScrollLeft,
    listScrollTop,
    returnBoardPath: typeof returnBoardPath === 'string' ? returnBoardPath : undefined,
  };
}

function storage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage;
}

function readReturn(key: string): BoardCardReturnState | undefined {
  const raw = storage()?.getItem(key);
  if (raw === undefined || raw === null) return undefined;

  try {
    return parseBoardCardReturn(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

function writeReturn(key: string, value: BoardCardReturnState): void {
  storage()?.setItem(key, JSON.stringify(value));
}

function takeReturn(key: string): BoardCardReturnState | undefined {
  const value = readReturn(key);
  storage()?.removeItem(key);
  return value;
}

export function saveCardOpenReturn(intent: BoardCardOpenIntent): void {
  writeReturn(`${CARD_RETURN_PREFIX}${intent.path}`, intent);
}

export function readCardOpenReturn(cardPath: string): BoardCardReturnState | undefined {
  return readReturn(`${CARD_RETURN_PREFIX}${cardPath}`);
}

export function moveCardReturnToBoard(cardPath: string, boardPath: string): void {
  const value = takeReturn(`${CARD_RETURN_PREFIX}${cardPath}`);
  if (value !== undefined) writeReturn(`${BOARD_RETURN_PREFIX}${boardPath}`, value);
}

export function takeBoardCardReturn(boardPath: string): BoardCardReturnState | undefined {
  return takeReturn(`${BOARD_RETURN_PREFIX}${boardPath}`);
}
